import { useState, useMemo, useCallback } from 'react';
import { PendingSale } from '../types/payment';

export interface PendingPaymentFilterState {
  globalSearch: string;
  createdBy: string;
  category: string;
  saleState: string;
  status: 'all' | 'PENDING' | 'APPROVED' | 'REJECTED';
  amountExact: string;
  amountMin: string;
  amountMax: string;
  cardBrand: string;
  last4: string;
  dateFrom: string;
  dateTo: string;
}

const initialFilterState: PendingPaymentFilterState = {
  globalSearch: '',
  createdBy: '',
  category: '',
  saleState: '',
  status: 'all',
  amountExact: '',
  amountMin: '',
  amountMax: '',
  cardBrand: '',
  last4: '',
  dateFrom: '',
  dateTo: ''
};

export const usePendingPaymentFilters = (pendingSales: PendingSale[]) => {
  const [filters, setFilters] = useState<PendingPaymentFilterState>(initialFilterState);

  // Update individual filter
  const updateFilter = useCallback((field: keyof PendingPaymentFilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(initialFilterState);
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'status') return value !== 'all';
      return value !== '';
    });
  }, [filters]);

  // Optimized filtering logic with memoization
  const filteredSales = useMemo(() => {
    if (!hasActiveFilters) return pendingSales;

    return pendingSales.filter(sale => {
      // Global search - searches across all text fields
      if (filters.globalSearch) {
        const searchTerm = filters.globalSearch.toLowerCase().trim();
        if (searchTerm) {
          const searchableText = [
            sale.id,
            sale.created_by || '',
            sale.first_name || '',
            sale.last_name || '',
            sale.category || '',
            sale.sale_state || '',
            sale.card_brand || '',
            sale.last4 || '',
            sale.status,
            sale.amount.toString(),
            sale.preauth_transactionid || ''
          ].join(' ').toLowerCase();
          
          if (!searchableText.includes(searchTerm)) {
            return false;
          }
        }
      }

      // Created by filter
      if (filters.createdBy) {
        if (sale.created_by !== filters.createdBy) {
          return false;
        }
      }

      // Category filter
      if (filters.category) {
        if (sale.category !== filters.category) {
          return false;
        }
      }

      // Sale state filter
      if (filters.saleState) {
        if (sale.sale_state !== filters.saleState) {
          return false;
        }
      }

      // Status filter
      if (filters.status !== 'all') {
        if (sale.status !== filters.status) {
          return false;
        }
      }

      // Amount filters
      const saleAmount = Number(sale.amount);
      
      // Exact amount filter
      if (filters.amountExact) {
        const exactAmount = parseFloat(filters.amountExact);
        if (!isNaN(exactAmount) && Math.abs(saleAmount - exactAmount) > 0.001) {
          return false;
        }
      }

      // Minimum amount filter
      if (filters.amountMin) {
        const minAmount = parseFloat(filters.amountMin);
        if (!isNaN(minAmount) && saleAmount < minAmount) {
          return false;
        }
      }

      // Maximum amount filter
      if (filters.amountMax) {
        const maxAmount = parseFloat(filters.amountMax);
        if (!isNaN(maxAmount) && saleAmount > maxAmount) {
          return false;
        }
      }

      // Card brand filter
      if (filters.cardBrand) {
        if (sale.card_brand?.toLowerCase() !== filters.cardBrand.toLowerCase()) {
          return false;
        }
      }

      // Last 4 digits filter
      if (filters.last4) {
        if (sale.last4 !== filters.last4) {
          return false;
        }
      }

      // Date range filters
      if (filters.dateFrom || filters.dateTo) {
        const saleDate = new Date(sale.created_at);
        
        if (filters.dateFrom) {
          const fromDate = new Date(filters.dateFrom);
          if (saleDate < fromDate) {
            return false;
          }
        }

        if (filters.dateTo) {
          const toDate = new Date(filters.dateTo);
          toDate.setHours(23, 59, 59, 999); // End of day
          if (saleDate > toDate) {
            return false;
          }
        }
      }

      return true;
    });
  }, [pendingSales, filters, hasActiveFilters]);

  // Filter statistics
  const filterStats = useMemo(() => {
    return {
      total: pendingSales.length,
      filtered: filteredSales.length,
      hidden: pendingSales.length - filteredSales.length
    };
  }, [pendingSales.length, filteredSales.length]);

  return {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filteredSales,
    filterStats
  };
};